import { useEffect, useState } from 'react';
import { useLang } from 'hooks/lang';

import { actions, UploadPrefix } from 'entities';

import { Card, Column, IconButton, LinearProgress, Loader, Tooltip } from 'components/Blocks';

import { XIcon, RefreshIcon, DocumentTextIcon } from 'components/Icons';

import { useToggleV2, useUser } from 'hooks';
import { UploadedFile } from 'types';

import { truncateText } from 'utils/text';
import { FileWithId } from '../types';
import { getID } from 'utils/id';

interface SingleFileProps {
  children?: never;
  handleDelete: () => void;
  file: FileWithId | UploadedFile;
  onUpload(params: { file: FileWithId; uploaded: UploadedFile }): void;
  prefix: UploadPrefix;
}

function SingleFile({ handleDelete, file, onUpload, prefix }: SingleFileProps) {
  const user = useUser();

  const [isError, showError] = useToggleV2();

  const { t } = useLang();

  const [loadingPercentage, setLoadingPercentage] = useState(0);

  const upload = actions.uploads.uploadFile.use({
    onSuccess: showError.turnOff,
    onError: showError.turnOn,
  });

  async function executeUpload() {
    if ((file as UploadedFile).url) return;

    const result = await upload.execute({
      file: file as FileWithId,
      prefix,
      onUploadProgress: ({ loaded, total }) => {
        setLoadingPercentage((loaded / (total || 100000000)) * 100);
      },
    });

    setLoadingPercentage(0);

    if (!result) return;

    // Note: this is to avoid the double upload, same ID on
    // dev due to StrictMode + useEffect. This component is
    // set to be refactored away of this pattern so this will be fixed then
    onUpload({
      file: file as FileWithId,

      uploaded: {
        id: getID(),
        name: file.name,
        url: result.url,
        uploadedAt: new Date().toISOString(),
        uploadedBy: user.id,
      },
    });
  }

  useEffect(() => {
    if ((file as UploadedFile)?.url) return;

    executeUpload();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card
      className="relative h-24 max-w-[120px] shrink-0 overflow-hidden rounded-md py-6"
      flow="column"
      gap="2"
      justifyContent="center"
      alignItems="center"
      spacing="small"
    >
      {upload.loading ? (
        <Loader type="dualRing" size={24} />
      ) : (
        <DocumentTextIcon size={24} color={isError ? 'destructive' : 'primary-light'} />
      )}

      {upload.loading && (
        <LinearProgress className="mt-1 h-1 w-[70%] shrink-0" value={loadingPercentage || 80} />
      )}

      {isError ? (
        <Column alignItems="center" gap="1">
          <span className="text-sm text-destructive">{t('error')}</span>

          <IconButton onClick={executeUpload} size="small">
            <RefreshIcon size={14} />
          </IconButton>
        </Column>
      ) : (
        <Tooltip
          content={<span className="whitespace-pre-wrap text-xxs">{file.name}</span>}
          className="p-1"
          disabled={file.name.length <= 15}
        >
          <span title={file.name} className="max-w-full text-wrap text-center text-xs">
            {truncateText(file.name, 15)}
          </span>
        </Tooltip>
      )}

      <IconButton
        type="button"
        className="group absolute right-1 top-1 p-0.5"
        onClick={handleDelete}
      >
        <XIcon size={12} className="group-hover:text-destructive" />
      </IconButton>
    </Card>
  );
}

export default SingleFile;
